import React, { useEffect, useRef, useState } from 'react';
import {
  Box,
  FormControl,
  FormControlLabel,
  FormGroup,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Popover,
  Select,
  Stack,
  TextField,
  Typography,
  Divider,
  Checkbox,
  Link,
  Chip,
  CircularProgress,
} from '@mui/material'; 
import { APP_CONFIG } from '../../../config/app.config';
import { ProductMode } from '@domain/product/models/ProductMode';
import FilterSelectOption from './FilterSelectOption';
import { ProductFilter } from '@domain/product/models/ProductFilter';
import { useProductsFilter } from '@features/products/hooks/useProductsFilter';
import ForecastGrade from '@features/timetravel/components/ForecastAnalytics/ForecastGrade';
import { useCollections } from '@features/collections';
import { useSuppliers } from '@features/suppliers/hooks/useSuppliers';
import { useProductMarketplaces } from '@features/products';
import { Close, Search } from '@mui/icons-material';
import type { ProductsFilterSectionKey } from './productFilterSectionKeys';
import { Icon, IconSize } from '@shared/icons';

export type { ProductsFilterSectionKey } from './productFilterSectionKeys';

interface ProductsFilterProps {
  buttonLabel?: string;
  onFilterChange?: (filterCriteria: ProductFilter) => void;
  /** When both are set, filter state is controlled by the parent instead of the global store */
  filterCriteria?: ProductFilter;
  updateFilter?: (criteria: ProductFilter) => void;
  /** Omit these filter sections from the UI (search bar + popover blocks). */
  disabledSections?: readonly ProductsFilterSectionKey[];
}

/** Section / row labels — theme primary text (body on surface) */
const sectionTitleSx = {
  fontWeight: 500,
  fontSize: '0.875rem',
  color: 'text.primary',
  mb: 1.5,
};

const ProductsFilter: React.FC<ProductsFilterProps> = ({
  onFilterChange,
  filterCriteria: controlledCriteria,
  updateFilter: controlledUpdate,
  disabledSections = [],
}) => {
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [showSearch, setShowSearch] = useState(false);
  const [collectionsExpanded, setCollectionsExpanded] = useState(false);

  const global = useProductsFilter();
  const isControlled = controlledCriteria !== undefined && controlledUpdate !== undefined;
  const filterCriteria = isControlled ? controlledCriteria : global.filterCriteria;
  const updateFilter = isControlled ? controlledUpdate : global.updateFilter;
  const { collections: collectionsResponse } = useCollections();
  const { suppliers } = useSuppliers();
  const { marketplaces, isLoading: marketplacesLoading } = useProductMarketplaces();

  const filterCriteriaRef = useRef(filterCriteria);
  useEffect(() => {
    filterCriteriaRef.current = filterCriteria;
  }, [filterCriteria]);

  const collections = collectionsResponse?.status === 'success' ? collectionsResponse.data : [];

  const applyFilter = (next: ProductFilter) => {
    filterCriteriaRef.current = next;
    updateFilter(next);
    onFilterChange?.(next);
  };

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
    const nextCriteria = filterCriteria || {};
    const selectedIds = nextCriteria.collectionIds ?? [];
    const mustShowAllCollections =
      collections.length > 3 &&
      selectedIds.some(id => !collections.slice(0, 3).some(c => c.id === id));
    setCollectionsExpanded(mustShowAllCollections);
  };

  const handleClose = () => {
    setAnchorEl(null);
    setCollectionsExpanded(false);
  };

  const manyCollections = collections.length > 3;
  const visibleCollections =
    manyCollections && !collectionsExpanded ? collections.slice(0, 3) : collections;

  const handleSearchChange = (value: string) => {
    const prev = filterCriteriaRef.current || {};
    applyFilter({ ...prev, search: value || undefined });
  };

  const handleSearchClick = () => {
    setShowSearch(!showSearch);
  };

  const forecastGradesOptions = [
    { value: 'D', label: 'D', color: APP_CONFIG.ui.theme.colors.error },
    { value: 'C', label: 'C', color: APP_CONFIG.ui.theme.colors.warning },
    { value: 'B', label: 'B', color: '#FFCC00' },
    { value: 'A', label: 'A', color: APP_CONFIG.ui.theme.colors.success },
    { value: 'NONE', label: 'None', color: APP_CONFIG.ui.theme.colors.secondaryText },
  ];

  const handleModeSelect = (mode: ProductMode | 'ALL', checked: boolean) => {
    const prev = filterCriteriaRef.current || {};
    if (mode === 'ALL') {
      applyFilter({
        ...prev,
        modes: checked ? [ProductMode.REVENUE_OPTIMIZED, ProductMode.BALANCED, ProductMode.COST_OPTIMIZED] : [],
      });
    } else if (checked) {
      applyFilter({
        ...prev,
        modes: [...(prev.modes || []), mode],
      });
    } else {
      applyFilter({
        ...prev,
        modes: prev.modes?.filter(m => m !== mode),
      });
    }
  };

  const handleForecastGradeChange = (grade: string) => {
    const prev = filterCriteriaRef.current || {};
    const forecastGrades = prev.forecastGrades?.includes(grade)
      ? prev.forecastGrades?.filter(g => g !== grade)
      : [...(prev.forecastGrades || []), grade];
    applyFilter({
      ...prev,
      forecastGrades: forecastGrades.length > 0 ? forecastGrades : undefined,
    });
  };

  const handleCollectionToggle = (collectionId: string) => {
    const prev = filterCriteriaRef.current || {};
    const ids = new Set(prev.collectionIds ?? []);
    if (ids.has(collectionId)) {
      ids.delete(collectionId);
    } else {
      ids.add(collectionId);
    }
    const next = Array.from(ids);
    applyFilter({
      ...prev,
      collectionIds: next.length > 0 ? next : undefined,
    });
  };

  const handleSupplierChange = (supplierId: string) => {
    const prev = filterCriteriaRef.current || {};
    applyFilter({
      ...prev,
      supplierIds: supplierId ? [supplierId] : undefined,
    });
  };

  const handleMarketplaceToggle = (marketplaceId: string) => {
    const prev = filterCriteriaRef.current || {};
    const next = new Set(prev.marketplaceIds ?? []);
    if (next.has(marketplaceId)) {
      next.delete(marketplaceId);
    } else {
      next.add(marketplaceId);
    }
    applyFilter({
      ...prev,
      noMarketplaceListing: undefined,
      marketplaceIds: next.size > 0 ? Array.from(next) : undefined,
    });
  };

  const open = Boolean(anchorEl);
  const id = open ? 'products-filter-popover' : undefined;

  const isDisabled = (section: ProductsFilterSectionKey) => disabledSections.includes(section);

  const hasPopoverSection =
    !isDisabled('supplier') ||
    !isDisabled('collections') ||
    !isDisabled('marketplaces') ||
    !isDisabled('modes') ||
    !isDisabled('forecastGrades');

  return (
    <div>
      <Stack direction="row" spacing={1} alignItems="center">
        {hasPopoverSection && (
          <IconButton onClick={handleClick} color="info" aria-label="Open filters">
            <Icon icon="Filter" size={IconSize.LARGE} />
          </IconButton>
        )}

        {!isDisabled('search') && (
          <Stack direction="row" spacing={1} alignItems="center">
            {showSearch && (
              <>
                <TextField
                  autoFocus
                  placeholder="Search by title or EAN"
                  variant="outlined"
                  fullWidth
                  size="small"
                  sx={{ width: 200 }}
                  value={filterCriteria?.search ?? ''}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleSearchChange(e.target.value)}
                />
                <IconButton onClick={handleSearchClick} color="info" aria-label="Close search">
                  <Icon icon="X01"  size={IconSize.LARGE} />
                </IconButton>
              </>
            )}

            {!showSearch && (
              <IconButton onClick={handleSearchClick} color="info" aria-label="Search products">
                <Icon icon="Search01"  size={IconSize.LARGE} />
              </IconButton>
            )}
          </Stack>
        )}
      </Stack>

      <Popover
        id={id}
        open={open && hasPopoverSection}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        PaperProps={{
          sx: {
            width: 360,
            maxWidth: 'calc(100vw - 24px)',
            borderRadius: 2,
            overflow: 'hidden',
          },
        }}
      >
        <Paper elevation={8} sx={{ borderRadius: 2, overflow: 'hidden' }}>


          <Box sx={{ maxHeight: 'min(70vh, 560px)', overflowY: 'auto' }}>
            <FormGroup>
              <Stack divider={<Divider />}>
              {!isDisabled('supplier') && (
              <Box sx={{ p: 2 }}>
                <Typography sx={sectionTitleSx}>Supplier</Typography>
                <FormControl fullWidth size="small">
                  <InputLabel id="supplier-filter-label">Supplier</InputLabel>
                  <Select
                    labelId="supplier-filter-label"
                    label="Supplier"
                    value={filterCriteria?.supplierIds?.[0] || ''}
                    onChange={e => handleSupplierChange(e.target.value as string)}
                    sx={{
                      '& .MuiSelect-select': {
                        padding: '8px 12px',
                      },
                    }}
                  >
                    <MenuItem value="">All suppliers</MenuItem>
                    {suppliers.map(supplier => (
                      <MenuItem key={supplier.id} value={supplier.id}>{supplier.name}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
              )}

              {!isDisabled('collections') && (
              <Box sx={{ p: 2 }}>
                <Typography sx={sectionTitleSx}>Collections</Typography>
                <Stack spacing={0}>
                  {visibleCollections.map(collection => {
                    const count = collection.products?.length ?? 0;
                    const checked =
                      filterCriteria?.collectionIds?.includes(collection.id) ?? false;
                    return (
                      <FormControlLabel
                        key={collection.id}
                        sx={{
                          alignItems: 'center',
                        }}
                        control={
                          <Checkbox
                            size="small"
                            checked={checked}
                            onChange={() => handleCollectionToggle(collection.id)}
                          />
                        }
                        label={
                          <Typography variant="body2" color="text.primary">
                            {collection.name}{' '}

                            ({count})

                          </Typography>
                        }
                      />
                    );
                  })}
                </Stack>
                {manyCollections && !collectionsExpanded && (
                  <Link
                    component="button"
                    type="button"
                    variant="body2"
                    onClick={() => setCollectionsExpanded(true)}
                    sx={{ mt: 1, cursor: 'pointer', fontWeight: 400, color: 'primary.main' }}
                  >
                    All collections
                  </Link>
                )}
                {manyCollections && collectionsExpanded && (
                  <Link
                    component="button"
                    type="button"
                    variant="body2"
                    onClick={() => setCollectionsExpanded(false)}
                    sx={{ mt: 1, cursor: 'pointer', fontWeight: 400, color: 'primary.main' }}
                  >
                    Show less
                  </Link>
                )}
              </Box>
              )}

              {!isDisabled('marketplaces') && (
              <Box sx={{ p: 2 }}>
                <Typography sx={sectionTitleSx}>Available on</Typography>
                {marketplacesLoading ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                    <CircularProgress size={24} />
                  </Box>
                ) : marketplaces.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">
                    No marketplaces
                  </Typography>
                ) : (
                  <Stack direction="row" gap={1} flexWrap="wrap" sx={{ columnGap: 1, rowGap: 1 }}>
                    {marketplaces.map(m => {
                      const selected = filterCriteria?.marketplaceIds?.includes(m.id) ?? false;
                      return (
                        <Chip
                          key={m.id}
                          label={m.name}
                          size="small"
                          clickable
                          color={selected ? 'primary' : 'info'}
                          onClick={() => handleMarketplaceToggle(m.id)}
                        />
                      );
                    })}
                  </Stack>
                )}
              </Box>
              )}

              {!isDisabled('modes') && (
              <Box sx={{ p: 2 }}>
                <Typography sx={sectionTitleSx}>Product mode</Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  <FilterSelectOption
                    label="Revenue Optimized"
                    checked={filterCriteria?.modes?.includes(ProductMode.REVENUE_OPTIMIZED) || false}
                    onChange={checked => handleModeSelect(ProductMode.REVENUE_OPTIMIZED, checked)}
                    color={APP_CONFIG.ui.theme.colors.success}
                  />
                  <FilterSelectOption
                    label="Balanced"
                    checked={filterCriteria?.modes?.includes(ProductMode.BALANCED) || false}
                    onChange={checked => handleModeSelect(ProductMode.BALANCED, checked)}
                    color={APP_CONFIG.ui.theme.colors.primary}
                  />
                  <FilterSelectOption
                    label="Cost Optimized"
                    checked={filterCriteria?.modes?.includes(ProductMode.COST_OPTIMIZED) || false}
                    onChange={checked => handleModeSelect(ProductMode.COST_OPTIMIZED, checked)}
                    color={APP_CONFIG.ui.theme.colors.error}
                  />
                  <FilterSelectOption
                    label="All"
                    checked={filterCriteria?.modes?.length === Object.values(ProductMode).length}
                    onChange={checked => handleModeSelect('ALL', checked)}
                    color={APP_CONFIG.ui.theme.colors.secondary}
                  />
                </Box>
              </Box>
              )}

              {!isDisabled('forecastGrades') && (
              <Box sx={{ p: 2 }}>
                <Typography sx={sectionTitleSx}>Forecast Grade</Typography>
                <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ columnGap: 1, rowGap: 1 }}>
                  {forecastGradesOptions.map(option => (
                    <ForecastGrade
                      key={option.value}
                      onClick={() => handleForecastGradeChange(option.value)}
                      disabled={!filterCriteria?.forecastGrades?.includes(option.value)}
                      grade={option.value}
                      size="small"
                    />
                  ))}
                </Stack>
              </Box>
              )}
              </Stack>
            </FormGroup>
          </Box>

       
        </Paper>
      </Popover>
    </div>
  );
};

export default ProductsFilter;
